/**
 * Turn a transcript into recommended CrewPilot tasks.
 *
 * This is a deliberate reader, not a silent executor. It only proposes a task
 * when someone is named as the person who "will" do something. Dates and
 * people are filled only when the match is unique. Location is filled only
 * when one project location's name appears as a whole word. Priority is left
 * empty unless the sentence itself says low, medium, or high.
 */

const WEEKDAYS = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function referenceUtc(referenceDate) {
  if (referenceDate && /^\d{4}-\d{2}-\d{2}$/.test(referenceDate)) {
    return new Date(`${referenceDate}T12:00:00Z`);
  }
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 12));
}

export function resolveRelativeDate(word, referenceDate) {
  const ref = referenceUtc(referenceDate);
  const key = String(word || "").toLowerCase();
  if (key === "today") return isoDate(ref);
  if (key === "tomorrow") {
    const next = new Date(ref);
    next.setUTCDate(next.getUTCDate() + 1);
    return isoDate(next);
  }
  if (!(key in WEEKDAYS)) return null;
  const delta = (WEEKDAYS[key] - ref.getUTCDay() + 7) % 7;
  const next = new Date(ref);
  next.setUTCDate(next.getUTCDate() + delta);
  return isoDate(next);
}

function sentenceTitle(action) {
  const clean = String(action || "").replace(/\s+/g, " ").replace(/[.,;:]+$/, "").trim();
  if (!clean) return "";
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function memberName(member) {
  return String(member?.employee_name || member?.name || member?.display_name || "").trim();
}

function matchMember(spokenName, members) {
  const wanted = spokenName.toLowerCase();
  const hits = (members || []).filter((member) => {
    const parts = memberName(member).toLowerCase().split(/\s+/).filter(Boolean);
    return parts.includes(wanted);
  });
  if (hits.length !== 1) return { member: null, ambiguous: hits.length > 1 };
  return { member: hits[0], ambiguous: false };
}

function matchLocation(sentence, locations) {
  const hits = (locations || []).filter((location) => {
    const name = String(location?.name || "").trim();
    if (name.length < 3) return false;
    const pattern = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    return pattern.test(sentence);
  });
  return hits.length === 1 ? hits[0] : null;
}

function priorityFromSentence(sentence) {
  const match = sentence.match(/\b(low|medium|high)\s+priority\b/i);
  return match ? match[1].toLowerCase() : null;
}

/**
 * @returns {{ recommendations: object[], notes: string[] }}
 */
export function analyzeConversation(transcript, { referenceDate, members = [], locations = [] } = {}) {
  const text = String(transcript || "").replace(/\s+/g, " ").trim();
  const notes = [];
  if (!text) {
    return { recommendations: [], notes: ["The transcript was empty, so nothing was proposed."] };
  }

  const sentences = text.split(/(?<=[.!?])\s+/).map((part) => part.trim()).filter(Boolean);
  const recommendations = [];

  sentences.forEach((sentence) => {
    const commitment = sentence.match(/^([A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\s+will\s+(.+)$/);
    if (!commitment) return;
    const spokenName = commitment[1];
    let action = commitment[2].replace(/[.!?]+$/, "").trim();
    let dueDate = null;
    const dated = action.match(
      /\b(?:by|on)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|tomorrow|today)\b/i,
    );
    const looseTomorrow = !dated && /\btomorrow\b/i.test(action);
    if (dated) {
      dueDate = resolveRelativeDate(dated[1], referenceDate);
      action = action.replace(dated[0], " ");
    } else if (looseTomorrow) {
      dueDate = resolveRelativeDate("tomorrow", referenceDate);
      action = action.replace(/\btomorrow\b/i, " ");
    }
    const title = sentenceTitle(action);
    if (!title) return;

    const { member, ambiguous } = matchMember(spokenName, members);
    const location = matchLocation(sentence, locations);
    const priority = priorityFromSentence(sentence);
    const inference = [];
    if (ambiguous) {
      inference.push(`${spokenName} matches more than one person, so the task is unassigned.`);
    } else if (!member) {
      inference.push(`${spokenName} was named, but no organization member matched, so the task is unassigned.`);
    }
    if (!dueDate) inference.push("No due date was stated.");
    if (!location) inference.push("No project location was confidently identified.");
    if (!priority) inference.push("Priority was not stated.");

    recommendations.push({
      title,
      description: sentence.replace(/[.!?]+$/, ""),
      assigneeId: member?.supabase_user_id || member?.userId || null,
      assigneeName: member ? memberName(member) : null,
      namedPerson: spokenName,
      dueDate,
      locationId: location?.project_location_id || location?.id || null,
      locationName: location?.name || null,
      priority,
      evidence: sentence,
      inferenceNotes: inference.join(" "),
    });
  });

  if (!recommendations.length) {
    notes.push("No commitment of the form “Name will …” was found. Nothing was turned into a task.");
  }
  return { recommendations, notes };
}

/** Fields passed to createProjectTask. Edits override the recommendation. */
export function toProjectTaskInput(recommendation, edits = {}) {
  const pick = (edited, inferred) => (edited !== undefined ? edited : inferred);
  return {
    title: pick(edits.title, recommendation.title),
    description: pick(edits.description, recommendation.description || ""),
    locationId: pick(edits.locationId, recommendation.locationId || ""),
    assigneeId: pick(edits.assigneeId, recommendation.assigneeId || ""),
    dueDate: pick(edits.dueDate, recommendation.dueDate || ""),
    priority: pick(edits.priority, recommendation.priority || ""),
  };
}
